import { getUserConsent, initKetch, initializeDataLayer, isConsentDenyAll, registerConsentListener, removeDisallowedCookies,
// waitForInitialConsent,
 } from "./ketch";
// Queue to hold method calls made before analytics has been loaded
const calls = [];
let projectName;
let initialized = false;
let logger = { log: () => { }, logError: () => { } };
let proxyConfig;
let analytics;
const getBaseSegmentIOSettings = () => {
    if (proxyConfig?.segmentAPIBaseURL) {
        return {
            apiHost: `${proxyConfig.segmentAPIBaseURL}/v1`,
        };
    }
    else {
        return true;
    }
};
// "registerConsentListener" is always triggered once after being set
// so we need to wait for the second call to listen for an actual change
const onConsentChange = (callback) => {
    let firstRun = true;
    registerConsentListener(() => {
        if (firstRun) {
            firstRun = false;
        }
        else {
            callback();
        }
    });
};
export const initializeAnalytics = ({ name, enableAnalytics, customLogger, proxy, ketchProperty, }) => {
    projectName = name;
    initialized = true;
    if (customLogger) {
        logger = customLogger;
    }
    proxyConfig = proxy;
    if (enableAnalytics) {
        analytics = {
            load,
            track,
            identify,
            page,
        };
        if (ketchProperty) {
            initKetch(ketchProperty);
        }
    }
    else {
        analytics = fakeAnalytics;
    }
};
const track = (action, properties) => {
    if (window.analytics) {
        window.analytics.track(action, properties, {
            segmentSource: projectName,
        });
        logger.log("debug", `Analytics: ${action} - ${JSON.stringify(properties)}`);
    }
    else {
        calls.push({
            method: "track",
            args: {
                action,
                properties,
            },
        });
    }
};
const identify = (userID, traits, options) => {
    if (window.analytics) {
        window.analytics.identify(userID, traits, options);
        logger.log("debug", `Analytics: Identify - ${JSON.stringify(traits, null, 2)}`);
    }
    else {
        calls.push({
            method: "identify",
            args: {
                userID,
                traits,
                options,
            },
        });
    }
};
const page = (properties) => {
    if (window.analytics) {
        const { pathname, search, ...rest } = properties;
        // Make sure the page event always have a trailing slash, this makes sure
        // they can be better aggregated in our analytics tools and are compatible
        // to all the next.js old events.
        let routePath = pathname;
        if (pathname !== undefined && pathname.slice(-1) !== "/") {
            routePath += "/";
        }
        window.analytics.page(`${routePath}${search}`, {
            path: routePath,
            search,
            ...rest,
        });
    }
    else {
        calls.push({
            method: "page",
            args: {
                ...properties,
            },
        });
    }
};
// Segment integration categories organized by consent category:
// adapted from https://github.com/segmentio/consent-manager/blob/v5.6.0/src/consent-manager/categories.ts
const SEGMENT_CATEGORIES_BY_CONSENT = {
    advertising: ["Advertising", "Tag Managers"],
    analytics_performance_and_customization: [
        "A/B Testing",
        "Analytics",
        "Attribution",
        "Email",
        "Enrichment",
        "Heatmaps & Recordings",
        "Performance Monitoring",
        "Personalization",
        "Raw Data",
        "Realtime Dashboards",
        "Referrals",
        "Surveys",
        "Video",
    ],
    essential: ["Livechat", "Security & Fraud"],
    functionality: [
        "CRM",
        "Customer Success",
        "Deep Linking",
        "Helpdesk",
        "SMS & Push Notifications",
    ],
};
// Returns the destinations enabled in Segment
const fetchSegmentIntegrations = async (writeKey) => {
    let cdnBaseURL;
    if (proxyConfig?.segmentCDNBaseURL) {
        cdnBaseURL = `https://${proxyConfig?.segmentCDNBaseURL}`;
    }
    else {
        cdnBaseURL = "https://cdn.segment.com";
    }
    const response = await fetch(`${cdnBaseURL}/v1/projects/${writeKey}/integrations`);
    if (response.ok) {
        return response.json();
    }
    else {
        logger.logError(new Error(`Unable to fetch Segment integrations. ${response.status} ${response.statusText}`));
        return [];
    }
};
const organizeIntegrationsByConsentCategory = (integrations) => {
    const result = {
        advertising: {},
        analytics_performance_and_customization: {},
        essential: {},
        functionality: {},
    };
    integrations.forEach(({ category, creationName }) => {
        let c = "advertising"; // default to "advertising"
        for (const [consentCategory, segmentCategories] of Object.entries(SEGMENT_CATEGORIES_BY_CONSENT)) {
            if (segmentCategories.includes(category)) {
                c = consentCategory;
            }
        }
        result[c][creationName] = true;
        const baseSegmentIOSettings = getBaseSegmentIOSettings();
        let segmentObject = result[c]["Segment.io"];
        if (typeof baseSegmentIOSettings === "boolean") {
            segmentObject = true;
        }
        else if (typeof segmentObject === "object") {
            segmentObject = {
                ...segmentObject,
                ...baseSegmentIOSettings,
            };
        }
        else if (Boolean(segmentObject)) {
            segmentObject = {
                ...baseSegmentIOSettings,
            };
        }
    });
    return result;
};
// Return the integrations that fall under the categories the user opted into
const getConsentedIntegrations = (integrations) => {
    // Cookie banner is not enabled in local dev so turn on all the integrations
    if (window.location.hostname === "localhost") {
        return {
            All: false,
            "Segment.io": getBaseSegmentIOSettings(),
            ...integrations.advertising,
            ...integrations.analytics_performance_and_customization,
            ...integrations.functionality,
            ...integrations.essential,
        };
    }
    const consent = getUserConsent();
    let result = {
        All: false,
        "Segment.io": !isConsentDenyAll(consent),
    };
    let purpose;
    for (purpose in consent) {
        if (consent[purpose]) {
            result = {
                ...result,
                ...integrations[purpose],
            };
        }
    }
    return result;
};
export const ANALYTICS_READY_EVENT = "analytics-ready";
// This event allows our instance of Intercom to know when Segment has loaded its
// instance of Intercom which overrides our callback
const dispatchEventOnSegmentReady = () => {
    window.analytics?.ready(() => {
        const event = new Event(ANALYTICS_READY_EVENT);
        window.dispatchEvent(event);
    });
};
let loaded = false;
const load = async ({ variation = "all", segmentKey, overrideOptions, reloadOnChange, }) => {
    // Helios injects segmentKey via webpack config, could be undefined in development
    // Otherwise should always be defined
    if (segmentKey) {
        // If user has never given/revoked consent (first-time visit), wait for their action
        // await waitForInitialConsent();
        const consent = getUserConsent();
        initializeDataLayer();
        // Remove cookies that are not allowed by the user's consent
        removeDisallowedCookies();
        // If the user has changed their consent, reload the page if necessary to apply changes
        onConsentChange(() => {
            if (reloadOnChange) {
                window.location.reload();
            }
        });
        let disableClientPersistence;
        if (variation === "minimal") {
            disableClientPersistence = true;
        }
        else {
            disableClientPersistence = !consent.functionality;
        }
        // https://segment.com/docs/connections/sources/catalog/libraries/website/javascript/#load-options
        let options = {
            // clientPersistence will allow the persistance of the user's analytics
            // ID during it's session if it is disabled, no data will be persisted
            // and the user will get a new ID in every session
            //
            // if user gave consent to persist functional cookies, allow
            // clientPersistence else, don't persist client's data
            //
            // https://segment.com/docs/connections/sources/catalog/libraries/website/javascript/persistence/#disable-all-client-side-persistence
            disableClientPersistence,
        };
        const segmentIntegrations = await fetchSegmentIntegrations(segmentKey);
        const consentedIntegrations = getConsentedIntegrations(organizeIntegrationsByConsentCategory(segmentIntegrations));
        if (variation === "minimal") {
            // At most only Mixpanel and Intercom should be turned on
            options = {
                ...options,
                integrations: {
                    All: false,
                    "Segment.io": consentedIntegrations["Segment.io"],
                    Mixpanel: consentedIntegrations["Mixpanel"],
                    Intercom: consentedIntegrations["Intercom"],
                    ...overrideOptions,
                },
            };
        }
        else {
            options = {
                ...options,
                integrations: {
                    ...consentedIntegrations,
                    ...overrideOptions,
                },
            };
        }
        if (!loaded) {
            loadSnippet({
                writeKey: segmentKey,
                cdnBaseURL: proxyConfig?.segmentCDNBaseURL || "cdn.segment.com",
                options,
            });
            logger.log("debug", `Analytics: Load - writeKey: ${segmentKey} - options: ${JSON.stringify(options, null, 2)}`);
            loaded = true;
            dispatchEventOnSegmentReady();
            // Call queued methods against segment analytics
            while (calls.length) {
                const item = calls.shift();
                if (item) {
                    const { method, args } = item;
                    if (method === "track") {
                        track(args.action, args.properties);
                    }
                    else if (method === "identify") {
                        identify(args.userID, args.traits, args.options);
                    }
                    else if (method === "page") {
                        page(args);
                    }
                }
            }
        }
        else {
            // `load` can only be called once per page load. Reload the page so
            // new options take effect. This should only happen when the
            // argument changes
            window.location.reload();
        }
    }
};
// Code adapted from the Segment JS snippet
// https://app.segment.com/memsql-ops/sources/customer_portal_development/overview
function loadSnippet({ writeKey, cdnBaseURL, options, }) {
    // type assertion: satisfies TS further down, but logic copied from segment snippet
    const snippetAnalytics = (window.analytics =
        window.analytics || []);
    const methods = [
        "trackSubmit",
        "trackClick",
        "trackLink",
        "trackForm",
        "pageview",
        "identify",
        "reset",
        "group",
        "track",
        "ready",
        "alias",
        "debug",
        "page",
        "screen",
        "once",
        "off",
        "on",
        "addSourceMiddleware",
        "addIntegrationMiddleware",
        "setAnonymousId",
        "addDestinationMiddleware",
        "register",
    ];
    function factory(method) {
        return function () {
            if (window.analytics?.initialized) {
                // @ts-ignore logic copied from segment snippet
                return window.analytics[method].apply(window.analytics, arguments);
            }
            const args = Array.from(arguments);
            if ([
                "track",
                "screen",
                "alias",
                "group",
                "page",
                "identify",
            ].includes(method)) {
                const c = document.querySelector("link[rel='canonical']");
                args.push({
                    __t: "bpc",
                    c: (c && c.getAttribute("href")) || undefined,
                    p: location.pathname,
                    u: location.href,
                    s: location.search,
                    t: document.title,
                    r: document.referrer,
                });
            }
            args.unshift(method);
            snippetAnalytics.push(args);
            return snippetAnalytics;
        };
    }
    for (const method of methods) {
        // @ts-ignore logic copied from segment snippet
        snippetAnalytics[method] = factory(method);
    }
    snippetAnalytics.load = function (key, opts) {
        const script = document.createElement("script");
        script.type = "text/javascript";
        script.async = true;
        script.setAttribute("data-global-segment-analytics-key", "analytics");
        script.src = `https://${cdnBaseURL}/analytics.js/v1/${key}/analytics.min.js`;
        document.getElementsByTagName("head")[0].prepend(script);
        snippetAnalytics._loadOptions = opts;
    };
    snippetAnalytics._writeKey = writeKey;
    snippetAnalytics.load(writeKey, options);
    return snippetAnalytics;
}
const fakeAnalytics = {
    load() { },
    track() { },
    identify() { },
    page() { },
};
export function getAnalytics() {
    if (initialized) {
        return analytics;
    }
    throw new Error("You must call initializeAnalytics before load");
}
//# sourceMappingURL=segment.js.map